<template>
    <div class="document-list-item">
        <Loading v-if="isLoading"/>
        <div class="document-list-item-image-container" @click="gotoPublicDocumentPage">
            <ImageLoader :default="require('@/assets/v3/img/document-default.png')" :src="imageUrl" :preventCache="false" :whiteSpinner="false"/>
            <i class="s-icon-zoom"/>
        </div>
        <div class="document-list-item-title">
            <span class="document-list-item-title-main">{{documentItem.organizationName}}</span>
            <span class="document-list-item-title-sub1">{{documentItem.folderTitle}}</span>
            <span class="document-list-item-title-sub2">{{documentItem.documentName}}</span>
        </div>
        <div class="document-list-item-actions">
            <template v-if="recipientSignatureRequested">
                <PrimaryButton :text="$t('document.list.item.actions.signDocument')" icon="s-icon-key" class="green primary no-sibling-margin" @click="signDocument"></PrimaryButton>
            </template>
            <template v-else-if="recipientSignaturePending">
                <div class="document-list-item-actions-signature-pending">{{ $t('document.list.item.actions.signaturePending') }}</div>
            </template>
            <template v-else-if="recipientSignatureComplete">
                <PrimaryButton :text="$t('document.list.item.actions.downloadRecipientSignature')" icon="s-icon-key" class="primary no-sibling-margin" @click="downloadRecipientSignature"></PrimaryButton>
            </template>
            <PrimaryButton :text="isBadge ? $t('document.list.item.actions.downloadBadge') : $t('document.list.item.actions.downloadDocument')" icon="s-icon-document_download" class="primary no-sibling-margin" @click="downloadDocument"></PrimaryButton>
        </div>
        <div class="document-list-item-share" v-if="isAdmin">
            <PrimaryButton class="small tertiary linkedin" @click="shareLinkedIn">
                <i class="sharing-icon-linkedin"/>
                <span>{{ $t('document.list.item.actions.addToLinkedIn') }}</span>
            </PrimaryButton>
            <PrimaryButton icon="sharing-icon-clipboard" class="small tertiary" @click="copyToClipboard"></PrimaryButton>
        </div>
        <div class="document-list-item-public" v-if="isAdmin">
            <el-switch
                v-model="documentItem.isPublicVisible"
                @change="onPublicVisibilityChange">
            </el-switch>
            <span class="document-list-item-public-desc">{{$t('document.list.item.publicAccessibility')}}</span>
        </div>

    </div>
</template>

<i18n>
{
    "en": {
        "document": {
            "list": {
                "item": {
                    "actions": {
                        "copyToClipboardSuccess": "The URL of the public page of the document was copied to your clipboard.",
                        "visibilityChangeSuccess": "The visibility of your document has been changed successfully.",
                        "visibilityChangeError": "An error occurred, please try again later.",
                        "addToLinkedIn": "Add to profile",
                        "downloadDocument": "Download document",
                        "downloadBadge": "Download badge",
                        "signDocument": "Sign document",
                        "signSuccess": "Document signed successfully.",
                        "signError": "An error occurred, please try again later.",
                        "downloadRecipientSignature": "Download your signature",
                        "signaturePending": "Recipient signature pending..."
                    },
                    "publicAccessibility": "publicly accessible"
                }
            }
        }
    },
    "es": {
        "document": {
            "list": {
                "item": {
                    "actions": {
                        "copyToClipboardSuccess": "La URL de la página pública del documento se ha copiado en el portapapeles.",
                        "visibilityChangeSuccess": "La visibilidad de su documento se ha modificado correctamente.",
                        "visibilityChangeError": "Se ha producido un error, inténtelo de nuevo más tarde.",
                        "addToLinkedIn": "Añadir al perfil",
                        "downloadDocument": "Descargar documento",
                        "downloadBadge": "Descargar insignia",
                        "signDocument": "Firmar documento",
                        "signSuccess": "Documento firmado correctamente.",
                        "signError": "Se ha producido un error, inténtelo de nuevo más tarde.",
                        "downloadRecipientSignature": "Descargar su firma",
                        "signaturePending": "Firma del destinatario pendiente..."
                    },
                    "publicAccessibility": "de acceso público"
                }
            }
        }
    },
    "fr": {
        "document": {
            "list": {
                "item": {
                    "actions": {
                        "copyToClipboardSuccess": "L'URL de la page publique du document a été copiée dans votre presse-papiers.",
                        "visibilityChangeSuccess": "La visibilité de votre document a été modifiée avec succès.",
                        "visibilityChangeError": "Une erreur s'est produite, veuillez réessayer plus tard.",
                        "addToLinkedIn": "Ajouter au profil",
                        "downloadDocument": "Télécharger le document",
                        "downloadBadge": "Télécharger le badge",
                        "signDocument": "Signer le document",
                        "signSuccess": "Document signé avec succès.",
                        "signError": "Une erreur s'est produite, veuillez réessayer plus tard.",
                        "downloadRecipientSignature": "Téléchargez votre signature",
                        "signaturePending": "Signature du destinataire en attente..."
                    },
                    "publicAccessibility": "accessible au public"
                }
            }
        }
    },
    "pt": {
        "document": {
            "list": {
                "item": {
                    "actions": {
                        "copyToClipboardSuccess": "O URL da página pública do documento foi copiado para a sua área de transferência.",
                        "visibilityChangeSuccess": "A visibilidade do seu documento foi alterada com sucesso.",
                        "visibilityChangeError": "Ocorreu um erro, tente novamente mais tarde.",
                        "addToLinkedIn": "Adicionar ao perfil",
                        "downloadDocument": "Descarregar o documento",
                        "downloadBadge": "Descarregar o badge",
                        "signDocument": "Assinar documento",
                        "signSuccess": "Documento assinado com sucesso.",
                        "signError": "Ocorreu um erro, tente novamente mais tarde.",
                        "downloadRecipientSignature": "Descarregar a sua assinatura",
                        "signaturePending": "Assinatura do destinatário pendente..."
                    },
                    "publicAccessibility": "acessível ao público"
                }
            }
        }
    },
    "de": {
        "document": {
            "list": {
                "item": {
                    "actions": {
                        "copyToClipboardSuccess": "Die URL der öffentlichen Seite des Dokuments wurde in Ihre Zwischenablage kopiert.",
                        "visibilityChangeSuccess": "Die Sichtbarkeit Ihres Dokuments wurde erfolgreich geändert.",
                        "visibilityChangeError": "Es ist ein Fehler aufgetreten, bitte versuchen Sie es später noch einmal.",
                        "addToLinkedIn": "Zum Profil hinzufügen",
                        "downloadDocument": "Dokument herunterladen",
                        "downloadBadge": "Badge herunterladen",
                        "signDocument": "Dokument unterschreiben",
                        "signSuccess": "Dokument erfolgreich signiert.",
                        "signError": "Es ist ein Fehler aufgetreten, bitte versuchen Sie es später noch einmal.",
                        "downloadRecipientSignature": "Laden Sie Ihre Unterschrift herunter",
                        "signaturePending": "Unterschrift des Empfängers steht noch aus..."
                    },
                    "publicAccessibility": "öffentlich zugänglich"
                }
            }
        }
    },
    "nl": {
        "document": {
            "list": {
                "item": {
                    "actions": {
                        "copyToClipboardSuccess": "De URL van de openbare pagina van het document is gekopieerd naar je klembord.",
                        "visibilityChangeSuccess": "De zichtbaarheid van je document is succesvol gewijzigd.",
                        "visibilityChangeError": "Er is een fout opgetreden, probeer het later nog eens.",
                        "addToLinkedIn": "Toevoegen aan profiel",
                        "downloadDocument": "Document downloaden",
                        "downloadBadge": "Badge downloaden",
                        "signDocument": "Document ondertekenen",
                        "signSuccess": "Document succesvol ondertekend.",
                        "signError": "Er is een fout opgetreden, probeer het later nog eens.",
                        "downloadRecipientSignature": "Uw handtekening downloaden",
                        "signaturePending": "Handtekening ontvanger in behandeling..."
                    },
                    "publicAccessibility": "publiek toegankelijk"
                }
            }
        }
    }
}
</i18n>

<script>
import ImageLoader from '@/components/v3/images/ImageLoader'
import Loading from '@/components/v3/loading/Loading.vue'
import PrimaryButton from '@/components/v3/buttons/PrimaryButton.vue'
import { documentServices } from '@/modules/documents/services/DocumentServices'

export default {
    components: {
        PrimaryButton,
        ImageLoader,
        Loading
    },
    props: {
        item: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            documentItem: this.item,
            isLoading: false
        }
    },
    computed: {
        imageUrl() {
            // if (this.documentItem) return this.$getTemplatePreviewUrl(this.documentItem.documentImageUrl, true, true)
            if (this.documentItem) return this.documentItem.documentImageUrl
            return ''

        },
        documentPublicPageUrl() {
            if (this.documentItem) return this.documentItem.documentPublicPageUrl
            return ''
        },
        documentPdfUrl() {
            if (this.documentItem) return this.documentItem.documentPdfUrl
            return ''
        },
        documentIssuerSignatureUrl() {
            if (this.documentItem) return this.documentItem.BlockChainReceiptUrl
            return ''
        },
        documentRecipientSignatureUrl() {
            if (this.documentItem) return this.documentItem.BlockChainReceipt2Url
            return ''
        },
        isBadge() {
            return this.documentPdfUrl.toLowerCase().includes('badge')
        },
        isAdmin() {
            return process.env.RECIPIENT_ADMIN
        },
        recipientSignatureRequested() {
            if (!this.documentItem) return false
            if (this.documentItem.isBlockChain2Approved) return false
            if (this.documentItem.IsBlockChainSigned2) return false
            return this.documentItem.isBlockChain2
        },
        recipientSignaturePending() {
            if (!this.documentItem) return false
            if (this.documentItem.IsBlockChainSigned2) return false
            return this.documentItem.isBlockChain2Approved
        },
        recipientSignatureComplete() {
            if (!this.documentItem) return false
            return this.documentItem.isBlockChain2Signed
        }
    },
    methods: {
        gotoPublicDocumentPage() {
            window.open(this.documentPublicPageUrl, '_blank')
        },
        shareLinkedIn() {
            window.open('https://www.linkedin.com/profile/add?startTask=SCHOOL_NAME', '_blank')
            // this.$emit('shareLinkedIn')
        },
        copyToClipboard() {
            navigator.clipboard.writeText(this.documentPublicPageUrl)
            this.$showNotification(
                this.$t('document.list.item.actions.copyToClipboardSuccess'),
                'success'
            )
        },
        onPublicVisibilityChange() {
            console.log('onPublicVisibilityChange', this.documentItem.isPrivate)
            this.isLoading = true
            documentServices.updateDocumentVisibility(this.documentItem.documentFileId, this.documentItem.isPublicVisible).then(result => {
                this.$showNotification(
                    this.$t('document.list.item.actions.visibilityChangeSuccess'),
                    'success'
                )
            }).catch(error => {
                this.$showNotification(
                    this.$t('document.list.item.actions.visibilityChangeError'),
                    'error'
                )
            }).finally(() => {
                this.isLoading = false
            })
        },
        downloadDocument() {
            // navigate to document url
            window.open(this.documentPdfUrl, '_blank')
        },
        signDocument() {
            console.log('signDocument', this.documentItem.documentFileId)
            this.isLoading = true
            documentServices.signDocument(this.documentItem.documentFileId).then(result => {
                this.$showNotification(
                    this.$t('document.list.item.actions.signSuccess'),
                    'success'
                )
                this.documentItem.isBlockChain2Approved = true
            }).catch(error => {
                this.$showNotification(
                    this.$t('document.list.item.actions.signError'),
                    'error'
                )
            }).finally(() => {
                this.isLoading = false
            })
        },
        downloadRecipientSignature() {
            console.log('downloadRecipientSignature', this.documentRecipientSignatureUrl)
            // open url to start downloading document blockchain receipt
            const url = this;$processUrl(this.documentRecipientSignatureUrl)
            window.open(url, '_blank')
        }
    }
}
</script>

<style lang="stylus">
@import "~assets/v3/css/variables.styl";

$selected-bg-margin = 8px
$selected-transition-duration = 0.15s

.document-list-item
    position relative
    display flex
    flex-direction column
    // width 360px
    flex 1 1 360px
    height auto
    font-size $list-item-font-size
    font-weight bold
    color black
    background-color white
    // basic-button-v2(1, 0)
    overflow hidden
    border-radius 7px
    box-shadow 0 2px 3px rgba(0, 0, 0, 0.15)
    transition all 0.25s cubic-bezier(0.16, 1, 0.3, 1)

    &.small
        max-width 228px

    &.selected

        outline 3px solid $color-primary
        transition outline $selected-transition-duration ease-in-out

        .document-list-item-title
            color $color-primary
            transition color $selected-transition-duration ease-in-out
            z-index 1

    // &:hover
    //     box-shadow 0 36px 10px -28px rgba(0, 0, 0, 0.15)
    //     transition all 0.35s cubic-bezier(.41,2.24,.61,1)

    .document-list-item-image-container
        width 100%
        display flex
        justify-content center
        align-items center
        cursor pointer
        position relative

        &:hover
            i
                opacity 1
                transform scale(1.25)

            img
                transform scale(1.125)

        i
            position absolute
            bottom 12px
            right 12px
            font-size 16px
            color black
            opacity 0.8
            transition all 0.25s
            background-color white
            padding 4px
            border-radius 100px
            opacity 0.8
            box-shadow 0 2px 2px rgba(0, 0, 0, 0.2)

        .image-loader
            width 100%
            height 100%
            display flex
            justify-content center
            align-items center
            overflow hidden

            img
                width auto
                max-width 100%
                max-height 240px
                transition all 0.25s


    .document-list-item-title
        display flex
        flex-direction column
        padding 18px 16px
        overflow hidden
        transition color $selected-transition-duration ease-in-out

        .document-list-item-title-main
            font-weight 700
            font-size 18px
            color #0080FF
            margin-bottom 4px

        .document-list-item-title-sub1
            font-weight 500
            font-size 16px
            color #323437
            margin-bottom 4px

        .document-list-item-title-sub2
            font-weight 500
            font-size 16px
            color #929292

    .document-list-item-actions
        display flex
        flex-direction column
        gap 4px
        padding 0 16px 18px 16px

        .document-list-item-actions-signature-pending
            box-sizing border-box
            padding 10px 16px
            display flex
            justify-content center
            align-items center
            background-color $color-grey-1
            color white
            border-radius 4px
            text-align center

        .primary-button
            flex 1 1 auto
            width 100%

            i
                font-size 18px

    .document-list-item-share
        display flex
        padding 0 16px 18px 16px
        justify-content space-between

        .primary-button

            &.linkedin
                flex 1 1 auto
                justify-content flex-start

                i
                    background-color #0a66c2 !important
                    color white
                    padding 6px 8px
                    border-radius 3px
                    margin-right 8px

    .document-list-item-public
        display flex
        padding 0 16px 18px 16px

        .document-list-item-public-desc
            font-size 14px
            font-weight normal
            margin-left 8px

</style>



// WEBPACK FOOTER //
// src/modules/documents/components/DocumentListItem.vue